/*
  Delivery checker (Cult Design PIM, 28 Oct 2026; markup in snippets/delivery-checker.liquid).

  Prices come from Shopify itself. A separate cart is made through the Storefront API (no token needed for carts)
  with this product, or with every line of the customer's cart, and the postcode and suburb as its delivery address;
  its delivery options are the ones checkout would offer: Shippit's live rates, the PIM's white glove rates, the
  store's own rates. The customer's own cart is never changed, and the request carries no cookies. Carrier rates
  arrive late, so the cart's delivery groups are asked for with @defer and read from Shopify's multipart answer.
  Suburbs come from delivery-checker-postcodes.js (window.HAY_POSTCODES: postcode -> [state, "Suburb|Suburb"]),
  loaded the first time a customer types a postcode.

  On a product page the checker lives in a drawer, opened from one line under the options. When the postcode is known
  (the customer's last check, kept in this browser, else a signed-in customer's address) the product is priced in the
  background as the page opens and the line reads "Delivery to 2204 from $207.90"; opening the drawer then shows every
  option straight away. A new colour or size is priced again.
*/
(function () {
  var API = "/api/2026-07/graphql.json";
  var TIMEOUT_MS = 20000;
  var STORE_KEY = "hay-delivery-checker";
  var QUERY = "mutation DeliveryCheck($input: CartInput!) { cartCreate(input: $input) { cart { id ... @defer { " +
    "deliveryGroups(first: 10, withCarrierRates: true) { nodes { " +
    "cartLines(first: 50) { nodes { merchandise { ... on ProductVariant { product { title } } } } } " +
    "deliveryOptions { title description deliveryMethodType estimatedCost { amount } } } } } } " +
    "userErrors { message } } }";

  var postcodesPromise = null;
  function loadPostcodes(url) {
    if (window.HAY_POSTCODES) return Promise.resolve(window.HAY_POSTCODES);
    if (!postcodesPromise) {
      postcodesPromise = new Promise(function (resolve, reject) {
        var s = document.createElement("script");
        s.src = url;
        s.async = true;
        s.onload = function () { window.HAY_POSTCODES ? resolve(window.HAY_POSTCODES) : reject(new Error("no postcodes")); };
        s.onerror = function () { postcodesPromise = null; reject(new Error("postcodes not loaded")); };
        document.head.appendChild(s);
      });
    }
    return postcodesPromise;
  }

  function remembered() {
    try { return JSON.parse(window.localStorage.getItem(STORE_KEY) || "null"); } catch (e) { return null; }
  }
  function remember(postcode, suburb, state) {
    try { window.localStorage.setItem(STORE_KEY, JSON.stringify({ postcode: postcode, suburb: suburb, state: state })); } catch (e) { /* private window */ }
  }

  function money(amount) {
    var n = Number(amount);
    if (!isFinite(n)) return "";
    if (n === 0) return "Free";
    return "$" + n.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" }[c];
    });
  }

  // Shopify's @defer answer is multipart/mixed: the first part has `data`, later parts `incremental` with the deferred
  // fields at a `path`. An answer that came back whole (plain JSON) is read the same way.
  function readAnswer(text, contentType) {
    var parts = [];
    var m = /boundary="?([^";]+)"?/i.exec(contentType || "");
    if (m) {
      var chunks = text.split("--" + m[1]);
      for (var i = 0; i < chunks.length; i++) {
        var start = chunks[i].indexOf("{");
        if (start < 0) continue;
        try { parts.push(JSON.parse(chunks[i].slice(start).replace(/\s*$/, ""))); } catch (e) { /* the closing marker */ }
      }
    } else {
      try { parts.push(JSON.parse(text)); } catch (e) { /* not an answer */ }
    }
    var groups = null, errors = [];
    for (var j = 0; j < parts.length; j++) {
      var p = parts[j];
      if (p.errors) for (var k = 0; k < p.errors.length; k++) errors.push(p.errors[k].message);
      var create = p.data && p.data.cartCreate;
      if (create) {
        if (create.userErrors) for (var u = 0; u < create.userErrors.length; u++) errors.push(create.userErrors[u].message);
        if (create.cart && create.cart.deliveryGroups) groups = create.cart.deliveryGroups.nodes;
      }
      var inc = p.incremental || [];
      for (var n = 0; n < inc.length; n++) {
        if (inc[n].errors) for (var e2 = 0; e2 < inc[n].errors.length; e2++) errors.push(inc[n].errors[e2].message);
        if (inc[n].data && inc[n].data.deliveryGroups) groups = inc[n].data.deliveryGroups.nodes;
      }
    }
    return { groups: groups, errors: errors };
  }

  function ask(lines, place) {
    var controller = window.AbortController ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, TIMEOUT_MS) : null;
    var body = JSON.stringify({ query: QUERY, variables: { input: {
      lines: lines,
      buyerIdentity: { countryCode: "AU", deliveryAddressPreferences: [{ deliveryAddress: {
        address1: "1 Main Street", city: place.suburb, province: place.state, country: "AU", zip: place.postcode } }] }
    } } });
    return fetch(API, {
      method: "POST", credentials: "omit", signal: controller ? controller.signal : undefined,
      headers: { "Content-Type": "application/json", "Accept": "multipart/mixed, application/json" }, body: body
    }).then(function (res) {
      return res.text().then(function (text) {
        if (timer) clearTimeout(timer);
        if (!res.ok) throw new Error("status " + res.status);
        return readAnswer(text, res.headers.get("content-type"));
      });
    }, function (err) { if (timer) clearTimeout(timer); throw err; });
  }

  // A mixed cart comes back from the Storefront API as one merged "Shipping" rate (the cheapest way for each shipment
  // added up), while checkout, with split shipping on, offers each shipment its own options. So each line is asked
  // alone to learn which shipment it travels in (lines with the same options travel together), then each shipment is
  // asked whole. Null when the cart is not split after all.
  function merged(groups, lines) {
    if (lines.length < 2 || !groups || groups.length !== 1) return false;
    var opts = groups[0].deliveryOptions || [];
    return opts.length === 1 && /^shipping$/i.test(String(opts[0].title || "").trim());
  }
  function askSplit(lines, place, alone) {
    return (alone || Promise.all(lines.map(function (l) { return ask([l], place); }))).then(function (answers) {
      var byKind = {}, order = [];
      answers.forEach(function (a, i) {
        var titles = [];
        (a.groups || []).forEach(function (g) { (g.deliveryOptions || []).forEach(function (o) { titles.push(o.title); }); });
        var kind = titles.sort().join("|") || "none";
        if (!byKind[kind]) { byKind[kind] = []; order.push(kind); }
        byKind[kind].push(i);
      });
      if (order.length < 2) return null;
      // A shipment of one line is already priced; only shipments of several lines are asked again, together.
      return Promise.all(order.map(function (k) {
        var idx = byKind[k];
        return idx.length === 1 ? Promise.resolve(answers[idx[0]]) : ask(idx.map(function (i) { return lines[i]; }), place);
      })).then(function (parts) {
        var groups = [];
        parts.forEach(function (p) { (p.groups || []).forEach(function (g) { groups.push(g); }); });
        return groups;
      });
    });
  }

  // The product form of the checker's own section (the drawer is moved to the end of the page, so the section is
  // found before it moves).
  function productLines(section) {
    var form = (section || document).querySelector("form[action*='/cart/add']") || document.querySelector("form[action*='/cart/add']");
    var idField = form && form.querySelector("[name='id']");
    var id = idField && idField.value;
    if (!id) return Promise.resolve([]);
    var qtyField = form.querySelector("[name='quantity']");
    var qty = Math.max(1, parseInt(qtyField && qtyField.value, 10) || 1);
    return Promise.resolve([{ merchandiseId: "gid://shopify/ProductVariant/" + id, quantity: qty }]);
  }
  function cartLines() {
    return fetch("/cart.js", { headers: { "Accept": "application/json" } }).then(function (r) { return r.json(); }).then(function (cart) {
      return (cart.items || []).filter(function (i) { return i.requires_shipping !== false; })
        .map(function (i) { return { merchandiseId: "gid://shopify/ProductVariant/" + i.variant_id, quantity: i.quantity }; });
    });
  }

  function init(root) {
    if (root.getAttribute("data-dc-ready")) return;
    root.setAttribute("data-dc-ready", "1");
    var mode = root.getAttribute("data-mode") || "product";
    var input = root.querySelector("[data-dc-postcode]");
    var select = root.querySelector("[data-dc-suburb]");
    var button = root.querySelector("[data-dc-check]");
    var note = root.querySelector("[data-dc-note]");
    var list = root.querySelector("[data-dc-list]");
    var postcodesUrl = root.getAttribute("data-postcodes");
    var contact = root.getAttribute("data-contact") || "/pages/contact";
    var section = root.closest("[data-section-id]");
    var busy = false;
    var state = null;
    var lastSuburb = null;

    // Product page: the line under the options and the drawer. The drawer moves to the end of the page so no
    // section's animation or overflow can trap it.
    var line = mode === "product" ? document.querySelector("[data-dc-line='" + root.id + "']") : null;
    var drawer = mode === "product" ? document.querySelector("[data-dc-drawer='" + root.id + "']") : null;
    var lineText = line && line.querySelector("[data-dc-line-text]");
    var opener = line && line.querySelector("[data-dc-open]");
    var panel = drawer && drawer.querySelector("[role='dialog']");
    if (drawer) document.body.appendChild(drawer);
    var known = null;
    var priceRun = 0;

    function setLine(text, action) {
      if (!line) return;
      lineText.textContent = text;
      opener.textContent = action;
      line.hidden = false;
    }
    // The line after a price: the cheapest option, "from" when there are several.
    function lineFrom(groups, postcode) {
      var costs = [];
      (groups || []).forEach(function (g) {
        (g.deliveryOptions || []).forEach(function (o) {
          var n = Number(o.estimatedCost && o.estimatedCost.amount);
          if (isFinite(n)) costs.push(n);
        });
      });
      if (!costs.length) return setLine("Delivery to " + postcode + " needs a quote", "Change");
      var least = Math.min.apply(null, costs);
      if (least === 0) return setLine("Free delivery to " + postcode, "Change");
      setLine("Delivery to " + postcode + (costs.length > 1 ? " from " : ", ") + money(least), "Change");
    }
    // A known postcode needs its state, and a suburb Shippit knows; both come from the postcode list when missing.
    function placeFor(k) {
      if (k.state && k.suburb) return Promise.resolve({ postcode: k.postcode, state: k.state, suburb: k.suburb });
      return loadPostcodes(postcodesUrl).then(function (all) {
        var hit = all[k.postcode];
        if (!hit) return null;
        var suburbs = hit[1].split("|");
        var want = String(k.suburb || "").toLowerCase();
        var suburb = suburbs.filter(function (s) { return s.toLowerCase() === want; })[0] || suburbs[0];
        return { postcode: k.postcode, state: hit[0], suburb: suburb };
      });
    }
    function priceLine() {
      if (!line || !known) return;
      var run = ++priceRun;
      setLine("Delivery to " + known.postcode, "Change");
      placeFor(known).then(function (place) {
        if (!place) return null;
        return productLines(section).then(function (lines) { return lines.length ? ask(lines, place) : null; });
      }).then(function (answer) {
        if (run === priceRun && answer && answer.groups) lineFrom(answer.groups, known.postcode);
      }).catch(function () { /* the line stays "Delivery to 2204"; the drawer still checks */ });
    }

    function onKey(e) { if (e.key === "Escape") closeDrawer(); }
    function openDrawer() {
      if (!drawer) return;
      drawer.hidden = false;
      document.documentElement.classList.add("delivery-drawer-open");
      window.requestAnimationFrame(function () { window.requestAnimationFrame(function () { drawer.classList.add("is-open"); }); });
      document.addEventListener("keydown", onKey);
      if (input.value.replace(/\D/g, "").length === 4 && !list.innerHTML) { panel.focus(); check(); } else { input.focus(); }
    }
    function closeDrawer() {
      if (!drawer || drawer.hidden) return;
      drawer.classList.remove("is-open");
      document.documentElement.classList.remove("delivery-drawer-open");
      document.removeEventListener("keydown", onKey);
      window.setTimeout(function () { drawer.hidden = true; }, 260);
      if (opener) opener.focus();
    }

    function say(text) { note.textContent = text || ""; }
    function clearResults() { list.innerHTML = ""; }

    function fillSuburbs(keep) {
      var code = input.value.replace(/\D/g, "").slice(0, 4);
      if (input.value !== code) input.value = code;
      state = null;
      select.hidden = true;
      select.innerHTML = "";
      if (code.length < 4) return Promise.resolve(false);
      return loadPostcodes(postcodesUrl).then(function (all) {
        var hit = all[code];
        if (!hit) { say("We can't find postcode " + code + ". Check the number and try again."); return false; }
        state = hit[0];
        var suburbs = hit[1].split("|");
        for (var i = 0; i < suburbs.length; i++) {
          var o = document.createElement("option");
          o.value = suburbs[i];
          o.textContent = suburbs[i];
          if (keep && suburbs[i].toLowerCase() === String(keep).toLowerCase()) o.selected = true;
          select.appendChild(o);
        }
        select.hidden = false;
        say("");
        return true;
      }, function () { say("Delivery prices can't be checked just now. You'll see them at checkout."); return false; });
    }

    function render(groups, place) {
      var options = 0;
      var html = "";
      var several = groups.length > 1;
      for (var g = 0; g < groups.length; g++) {
        var opts = (groups[g].deliveryOptions || []).slice().sort(function (a, b) {
          return Number(a.estimatedCost && a.estimatedCost.amount) - Number(b.estimatedCost && b.estimatedCost.amount);
        });
        if (!opts.length) continue;
        if (several) {
          var titles = [];
          var lines = (groups[g].cartLines && groups[g].cartLines.nodes) || [];
          for (var l = 0; l < lines.length; l++) {
            var t = lines[l].merchandise && lines[l].merchandise.product && lines[l].merchandise.product.title;
            if (t && titles.indexOf(t) < 0) titles.push(t);
          }
          var named = titles.length > 2 ? titles.slice(0, 2).join(", ") + " and " + (titles.length - 2) + " more" : titles.join(" and ");
          if (named) html += "<li class=\"delivery-checker__group\">" + escapeHtml(named) + "</li>";
        }
        for (var o = 0; o < opts.length; o++) {
          options += 1;
          var d = opts[o].description;
          html += "<li class=\"delivery-checker__option\"><span>" + escapeHtml(opts[o].title) +
            (d ? "<small>" + escapeHtml(d) + "</small>" : "") + "</span><span class=\"delivery-checker__price\">" +
            escapeHtml(money(opts[o].estimatedCost && opts[o].estimatedCost.amount)) + "</span></li>";
        }
      }
      list.innerHTML = html;
      if (!options) {
        note.innerHTML = "Delivery to " + escapeHtml(place.suburb) + " needs a quote. <a href=\"" + escapeHtml(contact) +
          "\">Contact us</a> and we'll price it for you.";
        return;
      }
      say("Delivery to " + place.suburb + " " + place.state + " " + place.postcode + (mode === "cart" ? ", for everything in your cart" : "") + ". Final price at checkout.");
    }

    function check() {
      if (busy) return;
      var code = input.value.replace(/\D/g, "");
      if (code.length !== 4) { clearResults(); say("Enter a 4-digit postcode."); input.focus(); return; }
      var ready = state && !select.hidden ? Promise.resolve(true) : fillSuburbs(lastSuburb);
      ready.then(function (ok) {
        if (!ok) return;
        var place = { postcode: code, state: state, suburb: select.value };
        remember(code, place.suburb, place.state);
        known = place;
        priceRun += 1;
        busy = true;
        button.disabled = true;
        clearResults();
        say("Checking delivery to " + place.suburb + "...");
        return (mode === "cart" ? cartLines() : productLines(section)).then(function (lines) {
          if (!lines.length) { say(mode === "cart" ? "Nothing in your cart needs delivery." : "Choose your options first, then check delivery."); return; }
          // Several lines may be a mixed cart: each line is asked alone at the same time, in case it is.
          var alone = lines.length > 1 ? Promise.all(lines.map(function (l) { return ask([l], place); })) : null;
          if (alone) alone.catch(function () { /* used only when the cart is mixed */ });
          return ask(lines, place).then(function (answer) {
            if (!answer.groups) throw new Error(answer.errors.join("; ") || "no delivery options");
            if (line) lineFrom(answer.groups, code);
            if (!merged(answer.groups, lines)) return render(answer.groups, place);
            return askSplit(lines, place, alone).then(function (split) { render(split && split.length ? split : answer.groups, place); });
          });
        }).catch(function () {
          clearResults();
          say("We couldn't load delivery prices just now. Try again in a moment, or see them at checkout.");
        }).then(function () { busy = false; button.disabled = false; });
      });
    }

    input.addEventListener("input", function () { lastSuburb = null; clearResults(); fillSuburbs(null); });
    // Inside the cart's form, Enter would send the form; here it checks delivery instead.
    input.addEventListener("keydown", function (e) { if (e.key === "Enter") { e.preventDefault(); check(); } });
    select.addEventListener("change", function () { clearResults(); say(""); });
    button.addEventListener("click", function (e) { e.preventDefault(); check(); });

    if (mode === "product") {
      // A new colour or size can travel differently: the drawer's prices go and the line is priced again. The theme
      // writes the new variant into the form's id field after the change event, so the price waits a moment.
      var repriceTimer = null;
      var stale = function () {
        if (list.innerHTML) { clearResults(); say(""); }
        window.clearTimeout(repriceTimer);
        repriceTimer = window.setTimeout(priceLine, 300);
      };
      if (window.jQuery) window.jQuery(document).on("variantChange", stale);
      var variants = section && section.querySelector(".product-form__variants");
      if (variants) variants.addEventListener("change", stale);
      if (opener) opener.addEventListener("click", openDrawer);
      if (drawer) {
        var closers = drawer.querySelectorAll("[data-dc-close]");
        for (var c = 0; c < closers.length; c++) closers[c].addEventListener("click", closeDrawer);
      }
    }

    // A postcode used before (else a signed-in customer's address) is filled in; its suburbs and the postcode list
    // load only when the customer acts. On a product page the line is priced for it straight away.
    var last = remembered();
    if (!(last && /^\d{4}$/.test(last.postcode || ""))) {
      var zip = String(root.getAttribute("data-customer-postcode") || "").replace(/\D/g, "");
      last = zip.length === 4 ? { postcode: zip, suburb: root.getAttribute("data-customer-suburb") || null, state: root.getAttribute("data-customer-state") || null } : null;
    }
    if (last && /^\d{4}$/.test(last.postcode || "")) {
      input.value = last.postcode;
      lastSuburb = last.suburb || null;
      var warm = function () { input.removeEventListener("focus", warm); if (!state) fillSuburbs(lastSuburb); };
      input.addEventListener("focus", warm);
      button.addEventListener("mouseenter", warm);
      known = { postcode: last.postcode, suburb: last.suburb || null, state: last.state || null };
      priceLine();
    } else {
      setLine("Delivery", "Enter postcode");
    }
  }

  function start() {
    var roots = document.querySelectorAll("[data-delivery-checker]");
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start); else start();
})();
